import { ChevronRight, type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /** Render-as link component (e.g. React Router's `<Link>` or `<NavLink>`). */
  active?: boolean;
}

interface AppShellProps {
  /** Markup rendered above all chrome (e.g. the super-admin banner). */
  topBanner?: ReactNode;
  /** Sidebar header — typically the org logo + name. */
  sidebarHeader: ReactNode;
  /** Top header — typically breadcrumbs + command-palette trigger + user menu. */
  topHeader: ReactNode;
  /** Sidebar nav rendered as a list of links; pass-through to consumer. */
  nav: ReactNode;
  /** Footer section rendered at the bottom of the sidebar (settings, user). */
  sidebarFooter?: ReactNode;
  /** Floating chrome (sync indicator, toaster). */
  overlays?: ReactNode;
  children: ReactNode;
  /** Sets `data-theme="super-admin"` on the shell to swap accents. */
  isSuperAdmin?: boolean;
}

/**
 * Two-column app shell described in DESIGN_SYSTEM.md §6. The sidebar is
 * fixed at 224px (it does not collapse in Phase 0; the icon-rail variant
 * lands when there's actually nav crowding). The header is 56px and
 * sticks to the top of the main column.
 */
export function AppShell({
  topBanner,
  sidebarHeader,
  topHeader,
  nav,
  sidebarFooter,
  overlays,
  children,
  isSuperAdmin = false,
}: AppShellProps) {
  return (
    <div
      className="flex min-h-screen flex-col bg-bg text-fg"
      data-super-admin={isSuperAdmin || undefined}
    >
      {topBanner}
      <div className="flex flex-1 overflow-hidden">
        <aside
          className={cn(
            'hidden w-[76px] shrink-0 flex-col bg-sidebar text-sidebar-fg md:flex xl:w-[232px]',
            isSuperAdmin && 'ring-1 ring-inset ring-accent/40',
          )}
        >
          <div className="flex h-[60px] items-center border-b border-white/10 px-3 xl:px-4">
            {sidebarHeader}
          </div>
          <nav className="flex flex-1 flex-col overflow-y-auto p-2">{nav}</nav>
          {sidebarFooter ? (
            <div className="border-t border-white/10 p-2">{sidebarFooter}</div>
          ) : null}
        </aside>
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="flex h-[60px] items-center gap-3 border-b border-border bg-bg px-4 xl:px-7">
            {topHeader}
          </header>
          <div className="flex-1 overflow-y-auto">
            <div className="w-full px-4 py-6 xl:px-7">{children}</div>
          </div>
        </main>
      </div>
      {overlays}
    </div>
  );
}

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Single action node — kept for backward compat with Phase 0 pages. */
  action?: ReactNode;
  /** Multiple actions; rendered after `action`. Use for primary + secondary buttons. */
  actions?: ReactNode;
  breadcrumbs?: { label: string; href?: string }[];
  /** Optional metadata row (pills, status chips) rendered under the title. */
  meta?: ReactNode;
  className?: string;
}

/** Standard page-header row used at the top of every authed route. */
export function PageHeader({
  title,
  description,
  action,
  actions,
  breadcrumbs,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-6 flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <nav className="flex items-center gap-1 text-xs text-fg-muted">
              {breadcrumbs.map((crumb, i) => (
                <span key={`${crumb.label}-${i}`} className="inline-flex items-center gap-1">
                  {crumb.href ? (
                    <a className="hover:text-fg" href={crumb.href}>
                      {crumb.label}
                    </a>
                  ) : (
                    <span>{crumb.label}</span>
                  )}
                  {i < breadcrumbs.length - 1 ? <ChevronRight className="h-3 w-3" aria-hidden /> : null}
                </span>
              ))}
            </nav>
          ) : null}
          <h1 className="truncate text-2xl font-bold tracking-tight">{title}</h1>
          {description ? <p className="text-[15px] text-fg-secondary">{description}</p> : null}
        </div>
        {action || actions ? (
          <div className="flex shrink-0 items-center gap-2">
            {action}
            {actions}
          </div>
        ) : null}
      </div>
      {meta ? <div className="flex flex-wrap items-center gap-2">{meta}</div> : null}
    </div>
  );
}

export type { NavItem };
